import { useState, useCallback } from "react";
import { toast } from "react-toastify";
import adminService from "../../api/services/adminService";
import axiosInstance from "../../api/services/axiosInstance";

export function usePromotionalMessage() {
  const [activeTab, setActiveTab] = useState("specific");
  const [searchEmail, setSearchEmail] = useState("");
  const [searchContactNo, setSearchContactNo] = useState("");
  const [searchedUser, setSearchedUser] = useState(null);
  const [message, setMessage] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const clearFeedback = useCallback(() => {
    setError("");
    setSuccessMsg("");
  }, []);

  const handleImageChange = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError("Image size should be less than 5MB");
      return;
    }
    setSelectedImage(file);
    setError("");
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  }, []);

  const handleRemoveImage = useCallback(() => {
    setSelectedImage(null);
    setImagePreview(null);
  }, []);

  const handleVideoChange = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      setError("Video size should be less than 50MB");
      return;
    }
    if (!file.type.startsWith("video/")) {
      setError("File must be a video");
      return;
    }
    setSelectedVideo(file);
    setError("");
    const reader = new FileReader();
    reader.onloadend = () => setVideoPreview(reader.result);
    reader.readAsDataURL(file);
  }, []);

  const handleRemoveVideo = useCallback(() => {
    setSelectedVideo(null);
    setVideoPreview(null);
  }, []);

  const uploadImage = useCallback(async () => {
    if (!selectedImage) return null;
    const formData = new FormData();
    formData.append("file", selectedImage);
    const response = await axiosInstance.post(
      "admin/users/upload/image",
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return response.data.imageUrl;
  }, [selectedImage]);

  const uploadVideo = useCallback(async () => {
    if (!selectedVideo) return null;
    const response = await adminService.uploadPromotionalVideo(selectedVideo);
    return response.videoUrl;
  }, [selectedVideo]);

  const handleSearchUser = useCallback(async () => {
    if (!searchEmail && !searchContactNo) {
      setError("Please enter email or contact number");
      return;
    }
    setSearching(true);
    setError("");
    setSearchedUser(null);
    try {
      const response = await adminService.searchUserForPromotionalMessage(
        searchEmail || null,
        searchContactNo || null
      );
      if (response.success) {
        setSearchedUser(response.user);
        setSuccessMsg("User found successfully!");
        setTimeout(() => setSuccessMsg(""), 3000);
      } else {
        setError(response.message || "User not found");
      }
    } catch (err) {
      setError(err?.message || "Error searching user");
    } finally {
      setSearching(false);
    }
  }, [searchEmail, searchContactNo]);

  const resetForm = useCallback(() => {
    setMessage("");
    setSelectedImage(null);
    setImagePreview(null);
    setSelectedVideo(null);
    setVideoPreview(null);
    setUrl("");
    setSearchedUser(null);
    setSearchEmail("");
    setSearchContactNo("");
  }, []);

  const handleSendToSpecificUser = useCallback(async () => {
    if (!searchedUser) {
      setError("Please search and select a user first");
      return;
    }
    if (!message && !selectedImage && !selectedVideo && !url) {
      setError("Please enter a message, select an image/video, or provide a URL");
      return;
    }
    setLoading(true);
    setError("");
    setSuccessMsg("");
    try {
      const imageUrl = selectedImage ? await uploadImage() : null;
      const videoUrl = selectedVideo ? await uploadVideo() : null;
      const response = await adminService.sendPromotionalMessageToUser({
        email: searchEmail || null,
        contactNo: searchContactNo || null,
        message: message || null,
        image: imageUrl,
        video: videoUrl,
        url: url || null,
      });
      if (response.success) {
        setSuccessMsg("Promotional message sent successfully!");
        toast.success("Promotional message sent successfully!");
        resetForm();
        setTimeout(() => setSuccessMsg(""), 3000);
      } else {
        setError(response.message || "Failed to send message");
      }
    } catch (err) {
      setError(err?.message || "Error sending promotional message");
    } finally {
      setLoading(false);
    }
  }, [
    searchedUser,
    message,
    selectedImage,
    selectedVideo,
    url,
    searchEmail,
    searchContactNo,
    uploadImage,
    uploadVideo,
    resetForm,
  ]);

  const handleSendToAllUsers = useCallback(async () => {
    if (!message && !selectedImage && !selectedVideo && !url) {
      setError("Please enter a message, select an image/video, or provide a URL");
      return;
    }
    const confirmSend = window.confirm(
      "Are you sure you want to send this promotional message to ALL users? This action cannot be undone."
    );
    if (!confirmSend) return;
    setLoading(true);
    setError("");
    setSuccessMsg("");
    try {
      const imageUrl = selectedImage ? await uploadImage() : null;
      const videoUrl = selectedVideo ? await uploadVideo() : null;
      const response = await adminService.sendPromotionalMessageToAll({
        message: message || null,
        image: imageUrl,
        video: videoUrl,
        url: url || null,
      });
      if (response.success) {
        setSuccessMsg(response.message);
        toast.success(response.message);
        resetForm();
        setTimeout(() => setSuccessMsg(""), 5000);
      } else {
        setError(response.message || "Failed to send messages");
      }
    } catch (err) {
      setError(err?.message || "Error sending promotional messages");
    } finally {
      setLoading(false);
    }
  }, [
    message,
    selectedImage,
    selectedVideo,
    url,
    uploadImage,
    uploadVideo,
    resetForm,
  ]);

  return {
    activeTab,
    setActiveTab,
    searchEmail,
    setSearchEmail,
    searchContactNo,
    setSearchContactNo,
    searchedUser,
    message,
    setMessage,
    selectedImage,
    imagePreview,
    selectedVideo,
    videoPreview,
    url,
    setUrl,
    loading,
    searching,
    error,
    setError,
    successMsg,
    clearFeedback,
    handleImageChange,
    handleRemoveImage,
    handleVideoChange,
    handleRemoveVideo,
    handleSearchUser,
    handleSendToSpecificUser,
    handleSendToAllUsers,
  };
}
